import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from 'react-google-autocomplete';
import { useJsApiLoader } from '@react-google-maps/api';
import { setLocation, setLocationName, setGoogleMapLoaded } from '../../Redux/Reducer/Reducer';
import { SetSessionStorageData, GetSessionStorageData } from '../../Utilities/Helpers/Helper';
import { AUTHENTICATION } from '../../Constants/Constants';

const libraries: any = ['places'];

interface GoogleLocation {
    lat: number;
    lng: number;
}

interface LocationDetails {
    city?: string;
    country?: string;
}

interface GoogleCountryDropdownProps {
    country: string;
    onChange?: (country: string) => void;
}

interface RootState {
    locationNameReducer: {
        locationName: string;
    };
    googleMapLoadedReducer?: {
        isGoogleMapLoaded: boolean;
    };
}

const GoogleCountryDropdown: React.FC<GoogleCountryDropdownProps> = ({ country, onChange }) => {
    const [ShowCountry, setShowCountry] = useState<string>('');
    const [isTyping, setIsTyping] = useState(false);
    const [isGeolocationRequested, setIsGeolocationRequested] = useState(false);
    const dispatch = useDispatch();

    const HeaderCountryName = useSelector((state: RootState) => state.locationNameReducer?.locationName);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY ?? '',
        libraries,
    });

    useEffect(() => {
        if (isLoaded) {
            dispatch(setGoogleMapLoaded(true));
        }
    }, [isLoaded, dispatch]);

    useEffect(() => {
        if (loadError) {
            console.error('Error loading Google Maps:', loadError);
        }
    }, [loadError]);

    const updateLocations = (location: GoogleLocation, details: LocationDetails) => {
        const displayName = details.city && details.country
            ? `${details.city}, ${details.country}`
            : details.country || '';


        dispatch(setLocation(location));
        dispatch(setLocationName(displayName));

        SetSessionStorageData(AUTHENTICATION.SHARED.CURRENT_LOCATION, JSON.stringify(location));
        if (details.country) SetSessionStorageData(AUTHENTICATION.SHARED.COUNTRY_NAME, details.country);
        if (details.city) SetSessionStorageData(AUTHENTICATION.SHARED.REGION_NAME, details.city);

        const locationJSON = JSON.stringify(location);
        SetSessionStorageData(AUTHENTICATION.SHARED.SCHOOL_LOCATION, locationJSON);
        SetSessionStorageData(AUTHENTICATION.SHARED.UNIVERSITY_LOCATION, locationJSON);
        SetSessionStorageData(AUTHENTICATION.SHARED.TUTOR_LOCATION, locationJSON);
        SetSessionStorageData(AUTHENTICATION.SHARED.ACTIVITIES_LOCATION, locationJSON);

        SetSessionStorageData('MAP_DRAG_UPDATE', 'false');
        setShowCountry(displayName);
    };

    useEffect(() => {
        const requestGeolocation = async () => {
            if (!isLoaded) {
                return;
            }

            if (!window.google || !window.google.maps) {
                setTimeout(requestGeolocation, 500);
                return;
            }

            const existingLocation = GetSessionStorageData(AUTHENTICATION.SHARED.CURRENT_LOCATION);
            const existingCountry = GetSessionStorageData(AUTHENTICATION.SHARED.COUNTRY_NAME);

            if (existingLocation && existingCountry) {
                const regionName = GetSessionStorageData(AUTHENTICATION.SHARED.REGION_NAME);
                setShowCountry(regionName ? `${regionName}, ${existingCountry}` : existingCountry);
                return;
            }

            if (isGeolocationRequested) return;
            setIsGeolocationRequested(true);


            if (!navigator.geolocation) {
                console.error('Geolocation not supported');
                setDubaiFallback();
                return;
            }

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const location: GoogleLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };


                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode(
                        { location: { lat: location.lat, lng: location.lng } },
                        (
                            results: google.maps.GeocoderResult[] | null,
                            status: google.maps.GeocoderStatus
                        ) => {
                            if (status === 'OK' && results && results[0]) {
                                const addressComponents = results[0].address_components;
                                const city = addressComponents.find((c: any) =>
                                    c.types.includes('locality') || c.types.includes('administrative_area_level_1')
                                );
                                const country = addressComponents.find((c: any) => c.types.includes('country'));

                                updateLocations(location, {
                                    city: city?.long_name,
                                    country: country?.long_name
                                });
                            } else {
                                updateLocations(location, {});
                            }
                        }
                    );
                },
                (error) => {
                    console.warn('Geolocation error:', error.message);
                    setDubaiFallback();
                },
                { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
            );
        };

        const setDubaiFallback = () => {
            const dubaiLocation: GoogleLocation = { lat: 25.2048, lng: 55.2708 };

            updateLocations(dubaiLocation, {
                city: 'Dubai',
                country: 'United Arab Emirates'
            });

            SetSessionStorageData('LOCATION_SOURCE', 'fallback');
        };

        const timer = setTimeout(requestGeolocation, 1000);
        return () => clearTimeout(timer);
    }, [isLoaded, isGeolocationRequested, dispatch]);

    useEffect(() => {
        if (!isTyping) {
            const regionName = GetSessionStorageData(AUTHENTICATION.SHARED.REGION_NAME);
            const countryName = GetSessionStorageData(AUTHENTICATION.SHARED.COUNTRY_NAME);

            if (regionName && countryName) {
                setShowCountry(`${regionName}, ${countryName}`);
            } else if (countryName) {
                setShowCountry(countryName);
            } else if (HeaderCountryName) {
                setShowCountry(HeaderCountryName);
            }
        }
    }, [HeaderCountryName, isTyping]);

    if (loadError) return <div>Error loading maps</div>;
    if (!isLoaded) return <div>Loading maps...</div>;

    return (
        <div>
            <Autocomplete
                apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
                onPlaceSelected={(place: any) => {
                    if (!place?.geometry?.location) return;

                    const location = {
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng()
                    };

                    const addressComponents = place.address_components || [];
                    const country = addressComponents.find((c: any) => c.types.includes('country'));
                    const city = addressComponents.find((c: any) =>
                        c.types.includes('locality') || c.types.includes('administrative_area_level_1')
                    );

                    if (country) {
                        updateLocations(location, {
                            city: city?.long_name,
                            country: country.long_name
                        });

                        SetSessionStorageData('LOCATION_SOURCE', 'user_selection');
                        setTimeout(() => sessionStorage.removeItem('LOCATION_SOURCE'), 3000);
                    }
                }}
                options={{
                    types: ['(regions)'],
                    fields: ['address_components', 'geometry', 'formatted_address']
                }}
                className="auto-complete my-country-dropdown-one"
                value={ShowCountry || ''}
                onChange={(e: any) => {
                    setIsTyping(true);
                    setShowCountry(e.target.value);
                }}
                onBlur={() => setIsTyping(false)}
                placeholder="Location"
            />
        </div>
    );
};

export default GoogleCountryDropdown;